import React, { useState, useRef, useEffect } from "react";
import "./CategoryMetrics.css";

// COMPONENT: Shows 3 cards for Airport, Entertainment, and Malls/Hospital metrics with order lists
const CategoryMetrics = ({ orders, categoryMetrics }) => {
  // STATE: Track selected category for full orders view
  const [selectedCategory, setSelectedCategory] = useState(null);
  // STATE: Pagination for tables
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(100);
  // STATE: Track scroll position
  const [scrollPosition, setScrollPosition] = useState(0);

  // REFS: For detecting outside clicks and scrolling
  const overlayRef = useRef(null);
  const contentRef = useRef(null);
  const tableContainerRef = useRef(null);
  const mainContainerRef = useRef(null); // NEW: Ref for main container

  // HELPER: Determine order status based on data
  const getOrderStatus = (order) => {
    if (
      (order.pGhtime && order.pGhtime.trim() !== "") ||
      (order.pJiesuan && order.pJiesuan > 0)
    ) {
      return "completed";
    } else {
      return "pending";
    }
  };

  // Converts a timestamp from China time (UTC+8) to Nairobi time (UTC+3)
  const chinaToNairobi = (chinaTimeString) => {
    if (!chinaTimeString) return "—";

    console.log("Original China time string:", chinaTimeString);

    // 1. Split date and time parts
    const [datePart, timePart] = chinaTimeString.split("T");
    console.log("Date part:", datePart, "Time part:", timePart);

    // 2. Extract numbers
    const [year, month, day] = datePart.split("-").map(Number);
    const [hour, minute, second] = timePart.split(":").map(Number);

    // 3. Convert China time (UTC+8) to UTC
    const dateInUTC = new Date(
      Date.UTC(year, month - 1, day, hour - 8, minute, second)
    );
    console.log("Date in UTC:", dateInUTC.toISOString());

    // 4. Format for Nairobi time
    const nairobiTime = new Intl.DateTimeFormat("en-KE", {
      timeZone: "Africa/Nairobi",
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).format(dateInUTC);

    console.log("Formatted Nairobi time:", nairobiTime);

    return nairobiTime;
  };

  // HELPER: Convert minutes to hours with proper formatting
  const formatDuration = (minutes) => {
    if (!minutes || minutes === 0) return "0 hrs";

    const hours = minutes / 60;

    if (hours < 1) {
      // Less than 1 hour, show in minutes
      return `${minutes} min`;
    } else if (hours === 1) {
      // Exactly 1 hour
      return "1 hr";
    } else if (hours < 24) {
      // Less than 24 hours, show decimal hours
      return `${hours.toFixed(1)} hrs`;
    } else {
      // 24 hours or more, show in days and hours
      const days = Math.floor(hours / 24);
      const remainingHours = (hours % 24).toFixed(1);

      if (remainingHours === "0.0") {
        return `${days} day${days > 1 ? "s" : ""}`;
      } else {
        return `${days} day${days > 1 ? "s" : ""} ${remainingHours} hrs`;
      }
    }
  };

  // HELPER: Format duration for table display (more detailed)
  const formatDurationForTable = (minutes) => {
    if (!minutes || minutes === 0) return "0 min";

    const hours = minutes / 60;

    if (hours < 1) {
      return `${minutes} min`;
    } else {
      const fullHours = Math.floor(hours);
      const remainingMinutes = Math.round((hours - fullHours) * 60);

      if (remainingMinutes === 0) {
        return `${fullHours} hr${fullHours > 1 ? "s" : ""}`;
      } else {
        return `${fullHours} hr${
          fullHours > 1 ? "s" : ""
        } ${remainingMinutes} min`;
      }
    }
  };

  // FUNCTION: Calculate stats for one category
  const calculateCategoryStats = (category, cabinetIds) => {
    const ordersInCategory = orders.filter((o) =>
      cabinetIds.includes(o.pCabinetid)
    );

    if (ordersInCategory.length === 0) {
      return {
        orderCount: 0,
        completedCount: 0,
        incompleteCount: 0,
        totalRevenue: 0,
        avgRevenue: 0,
        avgDuration: 0,
        avgDurationHours: 0, // NEW: Store duration in hours
        ordersInCategory: [],
      };
    }

    const completedOrdersInCategory = ordersInCategory.filter(
      (o) =>
        (o.pGhtime && o.pGhtime.trim() !== "") || (o.pJiesuan && o.pJiesuan > 0)
    );

    const incompleteCount =
      ordersInCategory.length - completedOrdersInCategory.length;

    const totalRevenue = completedOrdersInCategory.reduce(
      (sum, o) => sum + (o.pJiesuan || 0),
      0
    );

    const avgRevenue =
      completedOrdersInCategory.length > 0
        ? (totalRevenue / completedOrdersInCategory.length).toFixed(2)
        : 0;

    const totalDurationMinutes = ordersInCategory.reduce(
      (sum, o) => sum + (o.billingDuration || 0),
      0
    );

    // Calculate average duration in minutes (original)
    const avgDurationMinutes =
      ordersInCategory.length > 0
        ? (totalDurationMinutes / ordersInCategory.length).toFixed(1)
        : 0;

    // NEW: Calculate average duration in hours
    const avgDurationHours =
      ordersInCategory.length > 0
        ? (totalDurationMinutes / ordersInCategory.length / 60).toFixed(2)
        : 0;

    return {
      orderCount: ordersInCategory.length,
      completedCount: completedOrdersInCategory.length,
      incompleteCount,
      totalRevenue,
      avgRevenue: parseFloat(avgRevenue),
      avgDuration: parseFloat(avgDurationMinutes), // Keep original for compatibility
      avgDurationHours: parseFloat(avgDurationHours), // NEW: Average in hours
      ordersInCategory: ordersInCategory,
    };
  };

  // CARD DEFINITIONS with theme colors
  const categories = [
    {
      id: "ENT. FAC",
      label: "Entertainment",
      icon: "fas fa-theater-masks",
      color: "#ef2f6e", // amaranth
      gradient: "linear-gradient(135deg, #ef2f6e 0%, #ff4d8d 100%)",
    },
    {
      id: "AIRPORT",
      label: "Airports",
      icon: "fas fa-plane",
      color: "#5e52e0", // royal-blue
      gradient: "linear-gradient(135deg, #5e52e0 0%, #5448fa 100%)",
    },
    
    {
      id: "MALLS / HOSPITAL",
      label: "Malls & Hospitals",
      icon: "fas fa-building",
      color: "#4b6d22", // fern-frond
      gradient: "linear-gradient(135deg, #4b6d22 0%, #5a8c2e 100%)",
    },
  ];

  // Helper to get stats for all categories
  const allCategoryStats = categories.map((cat) => ({
    ...cat,
    stats: calculateCategoryStats(cat.id, categoryMetrics[cat.id] || []),
  }));

  // Pagination logic
  const getCurrentPageData = (data) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return data.slice(startIndex, endIndex);
  };

  const totalPages = (data) => Math.ceil(data.length / itemsPerPage);

  // NEW: Smart pagination - show limited page numbers
  const getVisiblePages = (data) => {
    const totalPageCount = totalPages(data);
    const maxVisiblePages = 7; // Show max 7 page numbers
    const current = currentPage;
    const pages = [];

    if (totalPageCount <= maxVisiblePages) {
      // If total pages is less than max visible, show all
      return Array.from({ length: totalPageCount }, (_, i) => i + 1);
    }

    // Always show first page
    pages.push(1);

    // Calculate start and end of visible pages
    let startPage = Math.max(2, current - 2);
    let endPage = Math.min(totalPageCount - 1, current + 2);

    // Adjust if we're near the start
    if (current <= 3) {
      endPage = Math.min(totalPageCount - 1, maxVisiblePages - 1);
    }

    // Adjust if we're near the end
    if (current >= totalPageCount - 2) {
      startPage = Math.max(2, totalPageCount - (maxVisiblePages - 2));
    }

    // Add ellipsis after first page if needed
    if (startPage > 2) {
      pages.push("...");
    }

    // Add middle pages
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    // Add ellipsis before last page if needed
    if (endPage < totalPageCount - 1) {
      pages.push("...");
    }

    // Always show last page
    if (totalPageCount > 1) {
      pages.push(totalPageCount);
    }

    return pages;
  };

  // UPDATED: Better scroll to top function (only for modal content)
  const scrollToTop = () => {
    if (!selectedCategory) return; // Only scroll in modal view

    const scrollOptions = { top: 0, behavior: "smooth" };

    // Method 1: Scroll the table container if it exists
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTo(scrollOptions);
    }

    // Method 2: Scroll the modal content area
    if (contentRef.current) {
      contentRef.current.scrollTo(scrollOptions);
    }

    // Method 3: Use setTimeout as backup for any rendering delays
    setTimeout(() => {
      if (tableContainerRef.current) {
        tableContainerRef.current.scrollTo(scrollOptions);
      }
    }, 100);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    // Scroll to top when page changes (only in modal)
    if (selectedCategory) {
      scrollToTop();
    }
  };

  // FUNCTION: Save scroll position before opening modal
  const saveScrollPosition = () => {
    setScrollPosition(window.pageYOffset || document.documentElement.scrollTop);
  };

  // FUNCTION: Restore scroll position when closing modal
  const restoreScrollPosition = () => {
    window.scrollTo({
      top: scrollPosition,
      behavior: "instant", // Use instant to avoid animation conflict
    });
  };

  // FUNCTION: Handle category card click
  const handleCategoryClick = (categoryId) => {
    saveScrollPosition(); // Save position before opening modal
    setSelectedCategory(categoryId);
    setCurrentPage(1); // Reset to first page when switching categories
    // Scroll to top when switching categories (within modal)
    setTimeout(scrollToTop, 150);
  };

  // FUNCTION: Handle back to categories grid
  const handleBackToGrid = () => {
    setSelectedCategory(null);
    setCurrentPage(1);
    // Restore scroll position after a brief delay to ensure modal is closed
    setTimeout(restoreScrollPosition, 10);
  };

  // FUNCTION: Handle category change from dropdown
  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value || null);
    setCurrentPage(1);
    // Scroll to top when switching categories (within modal)
    setTimeout(scrollToTop, 150);
  };

  // Get stats for selected category
  const getStatsForCategory = (catId) => {
    const cat = allCategoryStats.find((c) => c.id === catId);
    return cat ? cat.stats : { ordersInCategory: [] };
  };

  // Get selected category details
  const getSelectedCategory = () => {
    return categories.find((cat) => cat.id === selectedCategory);
  };

  // EFFECT: Handle outside clicks to close modal
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        selectedCategory &&
        overlayRef.current &&
        !contentRef.current.contains(event.target)
      ) {
        handleBackToGrid();
      }
    };

    if (selectedCategory) {
      document.addEventListener("mousedown", handleClickOutside);
      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "unset";
    };
  }, [selectedCategory]);

  // EFFECT: Prevent background scroll when modal is open
  useEffect(() => {
    if (selectedCategory) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [selectedCategory]);

  // Render the page
  if (!selectedCategory) {
    return (
      <div className="cm-metrics-container" ref={mainContainerRef}>
        <div className="cm-metrics-header">
          <h2 className="cm-section-title">
            <i className="fas fa-th-large pulse"></i> Metrics by Category
          </h2>
          <p className="cm-section-subtitle">
            Performance across different location types
          </p>
        </div>

        {/* Category Cards Grid */}
        <div className="cm-category-grid">
          {allCategoryStats.map((cat, idx) => (
            <div
              key={cat.id}
              className="cm-category-card"
              onClick={() => handleCategoryClick(cat.id)}
            >
              <div
                className="cm-card-glow"
                style={{ background: cat.gradient }}
              ></div>
              <div className="cm-card-content">
                <div className="cm-card-header">
                  <div
                    className="cm-card-icon"
                    style={{ background: cat.gradient }}
                  >
                    <i className={cat.icon}></i>
                  </div>
                  <h3 className="cm-card-title">{cat.label}</h3>
                </div>

                {/* VERTICAL STATS LAYOUT */}
                <div className="cm-vertical-stats">
                  <div className="cm-stat-row">
                    <div className="cm-stat-item">
                      <span className="cm-stat-label">Total Orders</span>
                      <span className="cm-stat-value">
                        {cat.stats.orderCount}
                      </span>
                    </div>
                  </div>

                  <div className="cm-stat-row">
                    <div className="cm-stat-item">
                      <span className="cm-stat-label">Completed</span>
                      <span className="cm-stat-value">
                        {cat.stats.completedCount}
                      </span>
                    </div>
                  </div>

                  <div className="cm-stat-row">
                    <div className="cm-stat-item">
                      <span className="cm-stat-label">Total Revenue</span>
                      <span className="cm-stat-value">
                        Ksh {cat.stats.totalRevenue.toLocaleString()}
                      </span>
                    </div>
                  </div>

                  <div className="cm-stat-row">
                    <div className="cm-stat-item">
                      <span className="cm-stat-label">Pending</span>
                      <span className="cm-stat-value">
                        {cat.stats.incompleteCount}
                      </span>
                    </div>
                  </div>

                  <div className="cm-stat-row">
                    <div className="cm-stat-item">
                      <span className="cm-stat-label">Avg Duration</span>
                      <span className="cm-stat-value">
                        {formatDuration(cat.stats.avgDuration)}
                      </span>
                    </div>
                  </div>

                  <div className="cm-stat-row">
                    <div className="cm-stat-item">
                      <span className="cm-stat-label">Avg Revenue</span>
                      <span className="cm-stat-value">
                        Ksh {cat.stats.avgRevenue}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="cm-card-footer">
                  <span className="cm-view-orders">
                    View Orders <i className="fas fa-arrow-right"></i>
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Selected category view - MODAL STYLE
  const selectedCat = getSelectedCategory();
  const stats = getStatsForCategory(selectedCategory);
  const currentData = getCurrentPageData(stats.ordersInCategory);
  const totalPageCount = totalPages(stats.ordersInCategory);
  const visiblePages = getVisiblePages(stats.ordersInCategory);

  return (
    <>
      {/* Overlay for outside clicks */}
      <div
        ref={overlayRef}
        className="cm-overlay"
        onClick={handleBackToGrid}
      ></div>

      <div className="cm-metrics-container cm-selected-modal" ref={contentRef}>
        {/* Header with Navigation */}
        <div className="cm-detail-header">
          <div className="cm-header-left">
            <button className="cm-back-btn" onClick={handleBackToGrid}>
              <i className="fas fa-arrow-left"></i> Back to Categories
            </button>
          </div>
          <div className="cm-header-center">
            <h2 className="cm-detail-title">
              <i
                className={selectedCat.icon}
                style={{ color: selectedCat.color }}
              ></i>
              {selectedCat.label}
              <span className="cm-record-count">
                ({stats.ordersInCategory.length} orders)
              </span>
            </h2>
          </div>
          <div className="cm-header-right">
            <div className="cm-category-selector">
              <select
                value={selectedCategory || ""}
                onChange={handleCategoryChange}
                className="cm-category-dropdown"
              >
                <option value="">Switch Category</option>
                {allCategoryStats.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Category Stats Overview - Hidden on mobile */}
        <div className="cm-category-overview cm-desktop-only">
          <div className="cm-overview-card">
            <div
              className="cm-overview-icon"
              style={{ background: selectedCat.gradient }}
            >
              <i className="fas fa-shopping-cart"></i>
            </div>
            <div className="cm-overview-content">
              <span className="cm-overview-value">{stats.orderCount}</span>
              <span className="cm-overview-label">Total Orders</span>
            </div>
          </div>
          <div className="cm-overview-card">
            <div
              className="cm-overview-icon"
              style={{ background: selectedCat.gradient }}
            >
              <i className="fas fa-check-circle"></i>
            </div>
            <div className="cm-overview-content">
              <span className="cm-overview-value">{stats.completedCount}</span>
              <span className="cm-overview-label">Completed</span>
            </div>
          </div>
          <div className="cm-overview-card">
            <div
              className="cm-overview-icon"
              style={{ background: selectedCat.gradient }}
            >
              <i className="fas fa-clock"></i>
            </div>
            <div className="cm-overview-content">
              <span className="cm-overview-value">{stats.incompleteCount}</span>
              <span className="cm-overview-label">Pending</span>
            </div>
          </div>
          <div className="cm-overview-card">
            <div
              className="cm-overview-icon"
              style={{ background: selectedCat.gradient }}
            >
              <i className="fas fa-money-bill-wave"></i>
            </div>
            <div className="cm-overview-content">
              <span className="cm-overview-value">
                Ksh {stats.totalRevenue.toLocaleString()}
              </span>
              <span className="cm-overview-label">Revenue</span>
            </div>
          </div>
          <div className="cm-overview-card">
            <div
              className="cm-overview-icon"
              style={{ background: selectedCat.gradient }}
            >
              <i className="fas fa-hourglass-half"></i>
            </div>
            <div className="cm-overview-content">
              <span className="cm-overview-value">
                {formatDuration(stats.avgDuration)}
              </span>
              <span className="cm-overview-label">Avg Duration</span>
            </div>
          </div>
          <div className="cm-overview-card">
            <div
              className="cm-overview-icon"
              style={{ background: selectedCat.gradient }}
            >
              <i className="fas fa-chart-line"></i>
            </div>
            <div className="cm-overview-content">
              <span className="cm-overview-value">Ksh {stats.avgRevenue}</span>
              <span className="cm-overview-label">Avg Revenue</span>
            </div>
          </div>
        </div>

        {/* Data Table - Always visible */}
        <div className="cm-data-section">
          <div className="cm-table-container" ref={tableContainerRef}>
            <table className="cm-data-table">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Cabinet ID</th>
                  <th>Location</th>
                  <th>Borrow Time</th>
                  <th>Duration</th>
                  <th>Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {currentData.map((order, index) => {
                  const status = getOrderStatus(order);
                  const statusLabel =
                    status === "completed" ? "Completed" : "Pending";
                  const globalIndex =
                    (currentPage - 1) * itemsPerPage + index + 1;

                  return (
                    <tr key={order.id || index} className="cm-table-row">
                      <td>
                        <span className="cm-order-id">
                          #{order.pOrderid || order.pAliorderid || globalIndex}
                        </span>
                      </td>
                      <td>{order.pCabinetid}</td>
                      <td>
                        <div className="cm-location-cell">
                          <i className="fas fa-map-marker-alt"></i>
                          {order.pShopName || "Unassigned"}
                        </div>
                      </td>
                      {/* <td>
                        <div className="cm-time-cell">
                          {new Date(order.pBorrowtime).toLocaleDateString()}
                          <span className="cm-time-small">
                            {new Date(order.pBorrowtime).toLocaleTimeString()}
                          </span>
                        </div>
                      </td> */}

                      <td>
                        <div className="time-cell">
                          {chinaToNairobi(order.pBorrowtime)}
                        </div>
                      </td>
                      <td>
                        <span className="cm-duration-badge">
                          {formatDurationForTable(order.billingDuration || 0)}
                        </span>
                      </td>
                      <td>
                        <span className="cm-amount-cell">
                          Ksh {(order.pJiesuan || 0).toFixed(2)}
                        </span>
                      </td>
                      <td>
                        <span className={`cm-status-badge ${status}`}>
                          <i
                            className={`fas fa-${
                              status === "completed" ? "check" : "clock"
                            }`}
                          ></i>
                          {statusLabel}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {stats.ordersInCategory.length === 0 && (
              <div className="cm-no-data">
                <i className="fas fa-inbox"></i>
                <h3>No Orders Found</h3>
                <p>
                  No orders available for {selectedCat.label} in the selected
                  date range.
                </p>
              </div>
            )}
          </div>

          {/* UPDATED Pagination */}
          {stats.ordersInCategory.length > 0 && (
            <div className="cm-pagination">
              <button
                className="cm-pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <i className="fas fa-chevron-left"></i> Previous
              </button>

              <div className="cm-page-numbers">
                {visiblePages.map((page, index) =>
                  page === "..." ? (
                    <span
                      key={`ellipsis-${index}`}
                      className="cm-page-ellipsis"
                    >
                      ...
                    </span>
                  ) : (
                    <button
                      key={page}
                      className={`cm-page-btn ${
                        currentPage === page ? "active" : ""
                      }`}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </button>
                  )
                )}
              </div>

              <button
                className="cm-pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPageCount}
              >
                Next <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CategoryMetrics;
